import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Search, Loader2, Wrench, FileText, Newspaper, Megaphone, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { trackSearch } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const SearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ services: [], pages: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (query.length >= 2) {
      const timer = setTimeout(() => {
        performSearch();
      }, 300); // Debounce
      return () => clearTimeout(timer);
    } else {
      setResults({ services: [], pages: [], total: 0 });
    }
  }, [query]);

  const performSearch = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`);
      setResults(response.data);
      
      // Track search in Google Analytics
      trackSearch(query);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (url) => {
    navigate(url);
    onClose();
    setQuery('');
  };

  const getIcon = (type) => {
    switch (type) {
      case 'service':
        return <Wrench className="w-4 h-4" />;
      case 'news':
        return <Newspaper className="w-4 h-4" />;
      case 'campaign':
        return <Megaphone className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'service':
        return 'Tjeneste';
      case 'news':
        return 'Nyhet';
      case 'campaign':
        return 'Kampanje';
      case 'service_page':
        return 'Tjenesteinfo';
      default:
        return 'Side';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'service':
        return 'bg-blue-600/20 text-blue-400 border-blue-600/50';
      case 'news':
        return 'bg-green-600/20 text-green-400 border-green-600/50';
      case 'campaign':
        return 'bg-purple-600/20 text-purple-400 border-purple-600/50';
      default:
        return 'bg-gray-600/20 text-gray-400 border-gray-600/50';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Search className="w-5 h-5 text-red-400" />
            <span>Søk</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Søk etter tjenester, nyheter, kampanjer..."
              className="bg-gray-800 border-gray-700 text-white pl-10 pr-10"
              autoFocus
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-red-400" />
              </div>
            ) : query.length < 2 ? (
              <div className="text-center py-8 text-gray-500">
                <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Skriv minst 2 tegn for å søke</p>
              </div>
            ) : results.total === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>Ingen resultater funnet for "{query}"</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Services */}
                {results.services.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-400 mb-2">TJENESTER</h3>
                    <div className="space-y-2">
                      {results.services.map((item, index) => (
                        <div
                          key={index}
                          onClick={() => handleResultClick(item.url)}
                          className="p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 cursor-pointer transition-colors group"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                {getIcon(item.type)}
                                <h4 className="font-semibold text-white group-hover:text-red-400 transition-colors">
                                  {item.title}
                                </h4>
                                <Badge className={getTypeColor(item.type)}>
                                  {getTypeLabel(item.type)}
                                </Badge>
                              </div>
                              {item.description && (
                                <p className="text-sm text-gray-400 line-clamp-1">{item.description}</p>
                              )}
                            </div>
                            {item.price && (
                              <div className="ml-4">
                                <span className="text-lg font-bold text-red-400">{item.price}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pages */}
                {results.pages.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-400 mb-2">SIDER</h3>
                    <div className="space-y-2">
                      {results.pages.map((item, index) => (
                        <div
                          key={index}
                          onClick={() => handleResultClick(item.url)}
                          className="p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 cursor-pointer transition-colors group"
                        >
                          <div className="flex items-center space-x-2 mb-1">
                            {getIcon(item.type)}
                            <h4 className="font-semibold text-white group-hover:text-red-400 transition-colors">
                              {item.title}
                            </h4>
                            <Badge className={getTypeColor(item.type)}>
                              {getTypeLabel(item.type)}
                            </Badge>
                          </div>
                          {item.excerpt && (
                            <p className="text-sm text-gray-400 line-clamp-2">{item.excerpt}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Result Count */}
                <div className="pt-2 border-t border-gray-700">
                  <p className="text-sm text-gray-500 text-center">
                    {results.total} resultat{results.total !== 1 ? 'er' : ''} funnet
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;
